// Horizontal scene strip — natively scrollable (user can swipe left/right
// freely on mobile, including inside the Android app) AND auto-drifts to the
// right when idle. The auto-drift is driven by JS incrementing scrollLeft,
// so it composes with native touch scrolling (unlike a CSS transform marquee
// which fights the user's finger).
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";

export interface SceneStripItem {
  id: string;
  label: string;
  image: string;
}

interface SceneStripProps {
  items: SceneStripItem[];
  onPick: (id: string) => void;
  selectedId?: string | null;
  className?: string;
  /** Auto-scroll speed in pixels per second. Default 150 px/s. */
  pxPerSec?: number;
}

export const SceneStrip = ({
  items,
  onPick,
  selectedId,
  className,
  pxPerSec = 150,
}: SceneStripProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const firstHalfRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const pausedUntilRef = useRef<number>(0);
  const posRef = useRef<number>(0);
  const userScrollRef = useRef<boolean>(false);

  useEffect(() => {
    const el = scrollerRef.current;
    const firstHalf = firstHalfRef.current;
    if (!el || !firstHalf || items.length === 0) return;

    const GAP = 10;
    // Start at the second copy so the user can swipe BOTH left and right
    // without hitting a hard edge. Crossing a loop boundary silently jumps
    // by one loop length onto visually identical pixels (no flicker).
    const initialLoop = firstHalf.offsetWidth + GAP;
    if (initialLoop > 0) {
      el.scrollLeft = initialLoop;
      posRef.current = initialLoop;
    } else {
      posRef.current = el.scrollLeft;
    }

    const onScroll = () => {
      if (!userScrollRef.current) return;
      const loop = firstHalf.offsetWidth + GAP;
      if (loop <= 0) {
        posRef.current = el.scrollLeft;
        return;
      }
      // Seamless wrap in BOTH directions while the user is dragging.
      if (el.scrollLeft <= 0) el.scrollLeft = loop;
      else if (el.scrollLeft >= loop * 2) el.scrollLeft = loop;
      posRef.current = el.scrollLeft;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const step = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      if (ts >= pausedUntilRef.current) {
        const loop = firstHalf.offsetWidth + GAP;
        posRef.current += pxPerSec * dt;
        if (loop > 0) {
          if (posRef.current >= loop * 2) posRef.current -= loop;
          else if (posRef.current < 0) posRef.current += loop;
        }
        el.scrollLeft = posRef.current;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [items.length, pxPerSec]);

  const pauseFor = (ms: number) => {
    userScrollRef.current = true;
    pausedUntilRef.current = performance.now() + ms;
    window.setTimeout(() => { userScrollRef.current = false; }, ms + 50);
  };

  if (items.length === 0) return null;

  const Card = ({ item }: { item: SceneStripItem }) => {
    const isActive = selectedId === item.id;
    return (
      <button
        type="button"
        onClick={() => onPick(item.id)}
        className={cn(
          "relative h-[140px] w-[112px] shrink-0 overflow-hidden rounded-2xl ring-1 ring-white/10 transition-all active:scale-95",
          isActive && "ring-2 ring-primary shadow-[0_0_24px_-4px_hsl(var(--primary))]",
        )}
      >
        <ImageWithSkeleton src={item.image} alt={item.label} eager />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <span className="absolute inset-x-0 bottom-2 px-2 text-center text-[11px] font-semibold leading-tight text-white drop-shadow">
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <div
      ref={scrollerRef}
      className={cn(
        "-mx-5 flex h-[140px] w-[calc(100%+2.5rem)] gap-[10px] overflow-x-auto overflow-y-hidden scrollbar-none",
        className,
      )}
      style={{
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
      onTouchStart={() => pauseFor(2500)}
      onPointerDown={() => pauseFor(2500)}
      onWheel={() => pauseFor(2500)}
      onMouseEnter={() => pauseFor(60_000)}
      onMouseLeave={() => pauseFor(0)}
    >
      <div ref={firstHalfRef} className="flex shrink-0 gap-[10px]">
        {items.map((item) => (
          <Card key={`a-${item.id}`} item={item} />
        ))}
      </div>
      <div className="flex shrink-0 gap-[10px]">
        {items.map((item) => (
          <Card key={`b-${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
};
